import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from 'axios';
import Sidebar from "./components/Sidebar";
import Navbar from './components/Navbar';
import Signin from './Common/Signin.jsx';
import Dashboard from "./pages/Dashboard/Dashboard.js";
import Rules from "./pages/Rules/rules.js";
import Announcement from "./pages/Announcements/announcement.js";
import Profile from "./pages/Profile/profile.js";
import Handbook from "./pages/Handbook/handbook.js";
import Payroll from "./pages/Payroll/payroll-report.js";
import Schedule from "./pages/Schedule/my-schedule.js";
import Salary from "./pages/Salary/salary.js";
import OverTime from "./pages/Overtime/overtime-report.js";
import WorkReports from "./pages/WorkReports/work-report.js";
import AddReport from "./pages/WorkReports/add-report.js";
import EditWorkReport from "./pages/WorkReports/edit-work-report.js";
import Settings from "./pages/AcoountSettings/account-settings.js";
import ResetPassword from "./pages/AcoountSettings/reset-password.js";
import TimeHistory from "./pages/TimeHistory/time-history.js";
import TimingsHistory from "./pages/TimeHistory/timings-history.js";
import ProvidentFund from "./pages/ProvidentFund/provident-fund.js";
import ProvidentFundHistory from "./pages/ProvidentFund/provident-fund-history.js";
import PendingLeave from "./pages/LeaveNew/pending-Leave.js";
import RejectedLeave from "./pages/Leave/rejected-leave.js";
import TotalLeave from "./pages/Leave/total-leave.js";
import ApplyLeave from "./pages/Leave/apply-leave.js";
import ApprovedLeave from "./pages/Leave/approved-leave.js";
import ProvidentFundReq from "./pages/ProvidentFund/provident-fund-req.js";
import PayinVoice from "./pages/Payroll/pay-invoice.js";
import Leaves from "./pages/Leave/Leaves.js";
import PasswordReset from "./pages/PasswordReset.js";
import ForgotPassword from "./pages/ForgotPassword.js";
import DeptAnnouncement from "./pages/DepartmentAnnouncements/departmentAnnouncements.js";
import EditLeave from "./pages/Leave/edit-leave.js";
import Documents from "./pages/documents.js";
import LeaveManagement from "./pages/Leave/LeaveManagement.js";
import NotAssigned from "./components/NotAssigned.jsx";
import TeamChart from "./pages/TeamChart.js"
import UploadForm from "./pages/UploadForm.js"
import LeaveLayout from "./components/LeaveLayout.jsx";
import LeaveBalance from "./pages/LeaveNew/LeaveBalance.jsx";
import AddLeave from "./pages/LeaveNew/addleave.js";
import LeaveDashboard from "./pages/ManagerLeaveSystem/LeaveManagement.js";
import UnseenAnnouncementsDialog from "./components/UnseenAnnouncementsDialog.js";
// ✅ NEW - Expense Module Imports
import ExpenseInfo from './pages/Expenses/ExpenseInfo.js';
import AddExpense from './pages/Expenses/AddExpense.js';
import EditExpense from './pages/Expenses/EditExpense.js';

// ============================================================
// ✅ EXPENSE MODULE ACCESS CONTROL
// Only this specific employee (User ID: 20, Username: AM-36)
// can see / open the 3 expense pages. Everyone else is blocked.
// ============================================================
const EXPENSE_ALLOWED_USERNAMES = ['am-36'];
const EXPENSE_ALLOWED_USER_IDS   = ['20'];
const EXPENSE_PATHS = [
  '/expense-info',
  '/add-expense',
  '/edit-expense/:expenseId',
];

// Check if the currently logged-in user is allowed to use Expenses
const isExpenseAllowedUser = () => {
  const userId = String(
    localStorage.getItem('userId') ||
    localStorage.getItem('user_id') ||
    localStorage.getItem('id') ||
    ''
  ).trim();

  const userName = String(
    localStorage.getItem('name') ||
    localStorage.getItem('username') ||
    localStorage.getItem('userName') ||
    ''
  ).trim().toLowerCase();

  return (
    EXPENSE_ALLOWED_USER_IDS.includes(userId) ||
    EXPENSE_ALLOWED_USERNAMES.includes(userName)
  );
};

const ProtectedRoute = ({ element, allowedPaths, isLoading }) => {
  const isAuthenticated = localStorage.getItem('accessToken') && localStorage.getItem('refreshToken');
  const location = useLocation();
  const currentPath = location.pathname.replace(/^\/+/, '');

  if (!isAuthenticated) {
    return <NotAssigned />;
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const normalizedAllowedPaths = allowedPaths.map(path => path.replace(/^\/+/, ''));
  const isAllowed = normalizedAllowedPaths.some(path =>
    currentPath === path || currentPath.startsWith(path.split('/:')[0])
  );

  return isAllowed ? element : <NotAssigned />;
};

const App = () => {
  const [allowedPaths, setAllowedPaths] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAllowedPaths = async () => {
      try {
        const response = await axios.get('https://backend.ems.hiradentallab.com/api/panel-pages/panel/EmployeePanel', {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': '0',
          },
        });

        let paths = response.data.flatMap(pp => [
          pp.page.path,
          ...(pp.page.subRoutes || []).map(subRoute => subRoute.path),
        ]);

        // ✅ Expense module: enforce access on the frontend too
        if (isExpenseAllowedUser()) {
          // Make sure AM-36 always has the expense paths available
          EXPENSE_PATHS.forEach(p => {
            if (!paths.includes(p)) paths.push(p);
          });
        } else {
          // Strip any expense path for everybody else so
          // ProtectedRoute below will block direct URL access
          paths = paths.filter(
            p => !String(p).toLowerCase().includes('expense')
          );
        }

        setAllowedPaths(paths);
      } catch (error) {
        setAllowedPaths(['/dashboard']);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllowedPaths();
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Signin />} />
        <Route path="password-reset" element={<PasswordReset />} />
        <Route path="forgotpassword/:userId/:token" element={<ForgotPassword />} />
        <Route path="/*" element={
          <ProtectedRoute
            allowedPaths={allowedPaths}
            isLoading={isLoading}
            element={<MainLayout />}
          />
        } />
      </Routes>
    </BrowserRouter>
  );
};

const MainLayout = () => {
  const [showUnseenDialog, setShowUnseenDialog] = useState(false);
  const [dialogChecked, setDialogChecked] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const shouldShow = localStorage.getItem('shouldShowUnseenDialog');
    const lastLoginTime = localStorage.getItem('lastLoginTime');
    const lastDialogShown = localStorage.getItem('lastDialogShownTime');

    if (shouldShow === 'true') {
      const shouldDisplay = !lastDialogShown || (lastLoginTime && parseInt(lastLoginTime) > parseInt(lastDialogShown));
      if (shouldDisplay) {
        setShowUnseenDialog(true);
        localStorage.setItem('lastDialogShownTime', Date.now().toString());
        localStorage.removeItem('shouldShowUnseenDialog');
      } else {
        localStorage.removeItem('shouldShowUnseenDialog');
      }
    }

    setDialogChecked(true);
  }, []);

  const handleNavigateToAnnouncements = (announcement = null) => {
    setShowUnseenDialog(false);
    if (announcement) {
      navigate('/emp-announcements', { state: { selectedAnnouncement: announcement } });
    } else {
      navigate('/emp-announcements');
    }
  };

  const handleClose = () => {
    setShowUnseenDialog(false);
  };

  return (
    <>
      <Sidebar />
      <div className="content-container">
        <Navbar />
        <Routes>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="handbook" element={<Handbook />} />
          <Route path="rules" element={<Rules />} />
          <Route path="emp-announcements" element={<Announcement />} />
          <Route path="profile" element={<Profile />} />
          <Route path="payroll-report" element={<Payroll />} />
          <Route path="schedule" element={<Schedule />} />
          <Route path="approved-leave" element={<ApprovedLeave />} />
          <Route path="rejected-leave" element={<RejectedLeave />} />
          <Route path="total-leave" element={<TotalLeave />} />
          <Route path="apply-leave" element={<ApplyLeave />} />
          <Route path="leave-management" element={<LeaveManagement />} />
          <Route path="my-leave-policy" element={<Leaves />} />
          <Route path="salary" element={<Salary />} />
          <Route path="overtime-report" element={<OverTime />} />
          <Route path="work-report" element={<WorkReports />} />
          <Route path="add-report" element={<AddReport />} />
          <Route path="my-edit-report/:reportId" element={<EditWorkReport />} />
          <Route path="account-settings" element={<Settings />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="time-history" element={<TimeHistory />} />
          <Route path="timings-history" element={<TimingsHistory />} />
          <Route path="provident-fund" element={<ProvidentFund />} />
          <Route path="provident-fund-history" element={<ProvidentFundHistory />} />
          <Route path="provident-fund-req" element={<ProvidentFundReq />} />
          <Route path="pay-invoice/:id" element={<PayinVoice />} />
          <Route path="my-dept-announcements" element={<DeptAnnouncement />} />
          <Route path="edit-leave/:id" element={<EditLeave />} />
          <Route path="documents" element={<Documents />} />
          <Route path="team-chart" element={<TeamChart />} />
          <Route path="upload" element={<UploadForm />} />
          <Route path="add-leave" element={<LeaveLayout />} />
          <Route path="addleave" element={<AddLeave />} />
          <Route path="/leave-balance" element={<LeaveBalance />} />
          <Route path="my-leaves-status" element={<PendingLeave />} />
          <Route path="leavemanager" element={<LeaveDashboard />} />

          {/* ✅ Expense Routes — restricted to AM-36 only */}
          <Route
            path="/expense-info"
            element={isExpenseAllowedUser() ? <ExpenseInfo /> : <NotAssigned />}
          />
          <Route
            path="/add-expense"
            element={isExpenseAllowedUser() ? <AddExpense /> : <NotAssigned />}
          />
          <Route
            path="/edit-expense/:expenseId"
            element={isExpenseAllowedUser() ? <EditExpense /> : <NotAssigned />}
          />
        </Routes>
      </div>
      
      <UnseenAnnouncementsDialog
        show={showUnseenDialog}
        onClose={handleClose}
        onNavigateToAnnouncements={handleNavigateToAnnouncements}
      />
    </>
  );
};

export default App;